// =============================================================================
// features/admin/feature-flags/RoleAxisSelect.tsx — Eixo "papéis" da audience
// de uma flag (F32-S04).
//
// Guarda role KEYS ('admin'), não UUID — é o único eixo de audience que não é
// UUID (ver apps/api/src/db/schema/featureFlags.ts). Mesmo vocabulário visual
// do UserRoleSelect (chips Badge + dropdown), mas chaveado por `key`.
// =============================================================================

import * as React from 'react';

import { Badge } from '../../../components/ui/Badge';
import { useRoles } from '../../../hooks/admin/useUsers';
import { ROLE_LABELS } from '../../../hooks/admin/useUsers.types';
import { cn } from '../../../lib/cn';

import { RemovableChip } from './RemovableChip';

interface RoleAxisSelectProps {
  value: string[];
  onChange: (roleKeys: string[]) => void;
  disabled?: boolean;
}

export function RoleAxisSelect({
  value,
  onChange,
  disabled = false,
}: RoleAxisSelectProps): React.JSX.Element {
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { roles, isLoading } = useRoles();

  React.useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  function label(key: string): string {
    return ROLE_LABELS[key] ?? key;
  }

  const available = roles.filter((r) => !value.includes(r.key));

  return (
    <div ref={containerRef} className="flex flex-col gap-2">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((key) =>
            disabled ? (
              <Badge key={key} variant={key === 'admin' ? 'warning' : 'info'}>
                {label(key)}
              </Badge>
            ) : (
              <RemovableChip
                key={key}
                variant={key === 'admin' ? 'warning' : 'info'}
                ariaLabel={`Remover papel ${label(key)}`}
                onRemove={() => onChange(value.filter((k) => k !== key))}
              >
                {label(key)}
              </RemovableChip>
            ),
          )}
        </div>
      )}

      {!disabled && (
        <div className="relative">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            disabled={isLoading || available.length === 0}
            aria-expanded={open}
            aria-haspopup="listbox"
            className={cn(
              'w-full flex items-center gap-2 px-3 py-2 rounded-sm text-sm text-left',
              'border border-border-strong text-ink-3',
              'hover:border-ink-3 hover:text-ink',
              'transition-[border-color,box-shadow] duration-fast ease',
              'font-sans font-medium',
              'disabled:opacity-40 disabled:cursor-not-allowed',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-azul/20',
            )}
            style={{
              background: 'var(--bg-elev-1)',
              boxShadow: 'inset 0 1px 2px var(--border-inner-dark)',
            }}
          >
            <svg
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.6}
              className="w-4 h-4 shrink-0"
              aria-hidden="true"
            >
              <path d="M8 3v10M3 8h10" />
            </svg>
            <span>
              {isLoading
                ? 'Carregando papéis…'
                : available.length === 0
                  ? 'Todos os papéis adicionados'
                  : 'Adicionar papel'}
            </span>
          </button>

          {open && available.length > 0 && (
            <div
              role="listbox"
              aria-label="Papéis disponíveis"
              className="absolute top-full left-0 mt-1 w-full rounded-sm border border-border z-20"
              style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--elev-3)' }}
            >
              {available.map((role) => (
                <button
                  key={role.key}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => {
                    onChange([...value, role.key]);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex items-center gap-2.5 w-full px-4 py-2.5',
                    'font-sans text-sm text-ink-2 hover:text-ink',
                    'hover:bg-surface-hover',
                    'transition-colors duration-fast text-left',
                    'focus-visible:outline-none focus-visible:bg-surface-hover',
                  )}
                >
                  <Badge variant={role.key === 'admin' ? 'warning' : 'info'}>
                    {label(role.key)}
                  </Badge>
                  <code className="font-mono text-xs text-ink-4 ml-auto">{role.key}</code>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
